import { Routes } from '@angular/router';
import { authGuard } from './core/guards/auth.guard';
import { storefrontEntitlementGuard } from './core/guards/storefront-entitlement.guard';
import { MainLayoutComponent } from './features/layout/main-layout/main-layout.component';

export const routes: Routes = [
    {
        path: 'login',
        loadComponent: () => import('./features/auth/login/login.component').then(m => m.LoginComponent),
        // login.component.html has no app-master-detail-layout and sits outside MainLayoutComponent
        data: { skeleton: 'form', listPanel: false }
    },
    {
        path: 'admin/login',
        loadComponent: () => import('./features/auth/login/login.component').then(m => m.LoginComponent),
        data: { skeleton: 'form', listPanel: false }
    },
    {
        path: '',
        component: MainLayoutComponent,
        canActivate: [authGuard],
        // This wrapper route also uses `component`, so app.routes.spec.ts's leaf
        // filter (loadComponent !== undefined || component !== undefined) counts it
        // as a route needing skeleton data too. Its flattened path is '' — identical
        // to the dashboard child's — so NavigationBusyService.findRouteData matches
        // this entry before ever recursing into children and returns this data
        // without reaching the dashboard child's. The value must therefore mirror
        // the dashboard child's exactly; it is not an independent declaration.
        data: { skeleton: 'dashboard', listPanel: true },
        children: [
            // Dashboard
            {
                path: '',
                loadComponent: () => import('./features/dashboard/dashboard.component').then(m => m.DashboardComponent),
                // dashboard.component.html: mode="split"
                data: { skeleton: 'dashboard', listPanel: true }
            },
            // Catalog
            {
                path: 'catalog',
                loadComponent: () => import('./features/catalog/catalog.component').then(m => m.CatalogComponent),
                // catalog.component.html: mode="split"
                data: { skeleton: 'cards', listPanel: true }
            },
            // Clients
            {
                path: 'clients',
                loadComponent: () => import('./features/clients/clients.component').then(m => m.ClientsComponent),
                // clients.component.html: mode="split"
                data: { skeleton: 'detail', listPanel: true }
            },
            // Orders
            {
                path: 'orders',
                loadComponent: () => import('./features/orders/orders.component').then(m => m.OrdersComponent),
                // orders.component.html: mode="full" — no list panel to reserve
                data: { skeleton: 'cards', listPanel: false }
            },
            // Settings
            {
                path: 'settings',
                loadComponent: () => import('./features/settings/settings.component').then(m => m.SettingsComponent),
                // settings.component.html: mode="full"
                data: { skeleton: 'form', listPanel: false }
            },
            // Options
            {
                path: 'options',
                loadComponent: () => import('./features/options/options.component').then(m => m.OptionsComponent),
                // options.component.html: mode="split"
                data: { skeleton: 'form', listPanel: true }
            },
            // Calendar
            {
                path: 'calendar',
                loadComponent: () => import('./features/calendar/calendar.component').then(m => m.CalendarComponent),
                // calendar.component.html: mode="split". Archetype overridden from the plan's
                // "cards": the details pane (calendar/components/form) renders a literal
                // <table> day/hour opening-hours grid, not a card grid.
                data: { skeleton: 'table', listPanel: true }
            },
            // Discount Rule
            {
                path: 'discount-rule',
                loadComponent: () => import('./features/discount-rule/discount-rule.component').then(m => m.DiscountRuleComponent),
                // discount-rule.component.html: mode="split". Archetype overridden from the
                // plan's "table": the details pane (discount-rule/components/form) is a
                // 14-field form with no table anywhere in the page.
                data: { skeleton: 'form', listPanel: true }
            },
            // Voucher
            {
                path: 'voucher',
                loadComponent: () => import('./features/voucher/voucher.component').then(m => m.VoucherComponent),
                // voucher.component.html: mode="split". Archetype overridden from the plan's
                // "table": the details pane (voucher/components/form) is dominated by 18 form
                // fields; the one embedded users-table is secondary.
                data: { skeleton: 'form', listPanel: true }
            },
            // Operator
            {
                path: 'operator',
                loadComponent: () => import('./features/operator/operator.component').then(m => m.OperatorComponent),
                // operator.component.html: mode="split". Archetype overridden from the plan's
                // "table": the details pane (operator/components/form) is a 10-field form
                // with no table.
                data: { skeleton: 'form', listPanel: true }
            },
            // Banner (client-level)
            {
                path: 'banner',
                loadComponent: () => import('./features/banner/banner.component').then(m => m.BannerComponent),
                // banner.component.html: mode="split". Archetype overridden from the plan's
                // "cards": the details pane (banner-form) is an image-upload-plus-fields form,
                // not a card grid.
                data: { skeleton: 'form', listPanel: true }
            },
            // Storefront homepage composition
            {
                path: 'storefront/homepage',
                canActivate: [storefrontEntitlementGuard],
                loadComponent: () => import('./features/storefront-homepage/storefront-homepage.component').then(m => m.StorefrontHomepageComponent),
                // storefront-homepage.component.html: mode="full"
                data: { skeleton: 'form', listPanel: false }
            },
            // Checkout recommendations
            {
                path: 'storefront/checkout-recommendations',
                canActivate: [storefrontEntitlementGuard],
                loadComponent: () => import('./features/checkout-recommendations/checkout-recommendations.component').then(m => m.CheckoutRecommendationsComponent),
                // checkout-recommendations.component.html: mode="full"
                data: { skeleton: 'form', listPanel: false }
            },
            // Marketing
            {
                path: 'marketing',
                loadComponent: () => import('./features/marketing/marketing.component').then(m => m.MarketingComponent),
                // marketing.component.html: mode="split". Archetype overridden from the plan's
                // "cards": the details pane (marketing-form) is a pure form; the campaign
                // stats grid lives in the list panel, not the details body.
                data: { skeleton: 'form', listPanel: true }
            },
            // Production
            {
                path: 'production',
                loadComponent: () => import('./features/production/production.component').then(m => m.ProductionComponent),
                // production.component.html: mode="split". Archetype overridden from the
                // plan's "form": the body is a production-grid of day-card cards; the few
                // mat-form-fields are just filter-toolbar controls, not the page content.
                data: { skeleton: 'cards', listPanel: true }
            },
            // Accounting (client) — merchant-owned exports only. Kept
            // separate from admin/accounting below: PLU-192 split the
            // formerly-shared route into two pure-role pages with no
            // admin-only tabs/behavior mixed in. Reached by both a genuine
            // CLIENT session and an ADMIN "connect as client" (impersonation)
            // session — same as every other route in this children array —
            // since "connect as client" already authenticates with a real,
            // tenant-scoped Client token (see sidebar.component.html's
            // FINANCE section comment).
            {
                path: 'accounting',
                loadComponent: () => import('./features/accounting/client-accounting/client-accounting.component').then(m => m.ClientAccountingComponent),
                data: { skeleton: 'table', listPanel: false }
            },
            // Action
            {
                path: 'action',
                loadComponent: () => import('./features/action/action.component').then(m => m.ActionComponent),
                // action.component.html: mode="full"
                data: { skeleton: 'form', listPanel: false }
            },
            // Partnership
            {
                path: 'partnership',
                loadComponent: () => import('./features/partnership/partnership.component').then(m => m.PartnershipComponent),
                // partnership.component.html: mode="split". Archetype overridden from the
                // plan's "cards": the details pane (partnership/components/form) is a pure
                // multi-field form.
                data: { skeleton: 'form', listPanel: true }
            },
            // Documentation
            {
                path: 'documentation',
                loadComponent: () => import('./features/documentation/documentation.component').then(m => m.DocumentationComponent),
                // documentation.component.html: mode="full", doc-grid of doc-card
                data: { skeleton: 'cards', listPanel: false }
            },
            // Admin section
            {
                path: 'admin/bank',
                loadComponent: () => import('./features/admin/bank/bank.component').then(m => m.AdminBankComponent),
                // admin/bank/bank.component.html: mode="full", grid-layout of bank-card
                data: { skeleton: 'cards', listPanel: false }
            },
            {
                path: 'admin/global-categories',
                loadComponent: () => import('./features/admin/global-categories/global-categories').then(m => m.GlobalCategoriesComponent),
                // global-categories.component.html: mode="full". Archetype overridden from
                // the plan's "table": the body is a hierarchical grid of category-card
                // elements; no table renders anywhere.
                data: { skeleton: 'cards', listPanel: false }
            },
            {
                path: 'admin/clients',
                loadComponent: () => import('./features/admin/clients/clients.component').then(m => m.AdminClientsComponent),
                // admin/clients/clients.component.html: mode="split"
                data: { skeleton: 'detail', listPanel: true }
            },
            {
                path: 'admin/import',
                loadComponent: () => import('./features/admin/import/import.component').then(m => m.AdminImportComponent),
                // import.component.html delegates to app-admin-import-form, whose template
                // (import-form.component.html) has mode="full"
                data: { skeleton: 'form', listPanel: false }
            },
            {
                path: 'admin/infos',
                loadComponent: () => import('./features/admin/infos/infos.component').then(m => m.AdminInfosComponent),
                // admin/infos/infos.component.html: mode="split"
                data: { skeleton: 'form', listPanel: true }
            },
            {
                path: 'admin/reinit',
                loadComponent: () => import('./features/admin/reinit/reinit.component').then(m => m.AdminReinitComponent),
                // reinit.component.html: <app-master-detail-layout> with no mode attribute,
                // which defaults to 'split'
                data: { skeleton: 'form', listPanel: true }
            },
            {
                path: 'admin/reviews',
                loadComponent: () => import('./features/admin/reviews/reviews.component').then(m => m.AdminReviewsComponent),
                // admin/reviews/reviews.component.html: mode="split", mat-table
                data: { skeleton: 'table', listPanel: true }
            },
            {
                path: 'admin/orders',
                loadComponent: () => import('./features/admin/orders/orders.component').then(m => m.AdminOrdersComponent),
                // admin/orders/orders.component.html: mode="full" — mixed alert-order-card
                // grid and per-site mat-tables; cards is the better first-paint approximation
                data: { skeleton: 'cards', listPanel: false }
            },
            {
                path: 'admin/products',
                loadComponent: () => import('./features/products/pages/product-list/product-list').then(m => m.ProductListComponent),
                title: 'Products Management',
                // product-list.html: mode="split". Archetype overridden from the plan's
                // "table": the details pane is a banner header plus a two-column info-grid,
                // not a data table.
                data: { skeleton: 'detail', listPanel: true }
            },

            {
                path: 'admin/clients/:uniq',
                loadComponent: () => import('./features/admin/clients/clients.component').then(m => m.AdminClientsComponent),
                data: { skeleton: 'detail', listPanel: true }
            },
            {
                // Fee receipts moved into /admin/accounting as a tab
                // (FeeReceiptsComponent); redirect kept for old bookmarks.
                path: 'admin/fee',
                redirectTo: 'admin/accounting'
            },
            {
                path: 'admin/excel',
                loadComponent: () => import('./features/excel/excel.component').then(m => m.ExcelComponent),
                // excel.component.html has no app-master-detail-layout at all — a single
                // upload mat-card
                data: { skeleton: 'form', listPanel: false }
            },
            {
                path: 'admin/banner',
                loadComponent: () => import('./features/banner/banner.component').then(m => m.BannerComponent),
                // same BannerComponent/template as 'banner' above
                data: { skeleton: 'form', listPanel: true }
            },
            {
                path: 'admin/loyalty',
                loadComponent: () => import('./features/admin/loyalty/loyalty.component').then(m => m.LoyaltyComponent),
                // admin/loyalty/loyalty.component.html: mode="full", config form plus a
                // mat-table of recent activity
                data: { skeleton: 'table', listPanel: false }
            },
            {
                path: 'admin/vocations',
                loadComponent: () => import('./features/admin/vocations/vocations.component').then(m => m.VocationsComponent),
                // vocations.component.html: mode="full". Archetype overridden from the
                // plan's "form": the body is the same hierarchical category-card grid as
                // admin/global-categories; the create/edit form lives in a modal, not the
                // route body.
                data: { skeleton: 'cards', listPanel: false }
            },
            {
                path: 'admin/drivers',
                loadComponent: () => import('./features/admin/drivers/drivers.component').then(m => m.AdminDriversComponent),
                // drivers.component.html: <app-master-detail-layout [loading]="loading">
                // with no mode attribute, which defaults to 'split'
                data: { skeleton: 'cards', listPanel: true }
            },
            {
                path: 'admin/payouts',
                loadComponent: () => import('./features/admin/payouts/payouts.component').then(m => m.AdminPayoutsComponent),
                // admin/payouts/payouts.component.html: mode="full", premium-table
                data: { skeleton: 'table', listPanel: false }
            },
            // Accounting (admin) — platform-wide Overview/Invoices/Exports/
            // Fee Audit. Separate page from client accounting above; see
            // that route's comment for why.
            {
                path: 'admin/accounting',
                loadComponent: () => import('./features/accounting/admin-accounting/admin-accounting.component').then(m => m.AdminAccountingComponent),
                data: { skeleton: 'table', listPanel: false }
            },
            {
                path: 'admin/leads',
                loadComponent: () => import('./features/admin/leads/leads.component').then(m => m.AdminLeadsComponent),
                // admin/leads/leads.component.html: mode="full", mat-table
                data: { skeleton: 'table', listPanel: false }
            },

            // Route Optimization
            {
                path: 'admin/route-optimization',
                loadComponent: () => import('./features/route-optimization/route-optimization.component').then(m => m.RouteOptimizationComponent),
                // route-optimization.component.html: mode="full", KPI metrics-bar
                data: { skeleton: 'cards', listPanel: false }
            },

            // Cargo — hidden, moved to separate system
            // { path: 'admin/cargo-pricing', loadComponent: () => import('./features/admin/cargo-pricing/cargo-pricing.component').then(m => m.CargoPricingComponent) },
            // { path: 'admin/cargo-quotes', loadComponent: () => import('./features/admin/cargo-quotes/cargo-quotes.component').then(m => m.CargoQuotesComponent) },

            // Inventory
            {
                path: 'inventory/suppliers',
                loadComponent: () => import('./features/inventory/suppliers/suppliers').then(m => m.SuppliersComponent),
                // suppliers.html: mode="full", mat-table
                data: { skeleton: 'table', listPanel: false }
            },
            {
                path: 'inventory/receive',
                loadComponent: () => import('./features/inventory/stock-reception/stock-reception').then(m => m.StockReceptionComponent),
                // stock-reception.html: mode="full". Archetype overridden from the plan's
                // "table": the body is a product-search-then-quantity form; no table renders.
                data: { skeleton: 'form', listPanel: false }
            },
            {
                path: 'inventory/stock-levels',
                loadComponent: () => import('./features/inventory/stock-levels/stock-levels').then(m => m.StockLevelsComponent),
                // stock-levels.html: mode="full", mat-table
                data: { skeleton: 'table', listPanel: false }
            },
            {
                path: 'inventory/movements',
                loadComponent: () => import('./features/inventory/stock-movements/stock-movements').then(m => m.StockMovementsComponent),
                // stock-movements.html: <app-master-detail-layout> with no mode attribute,
                // which defaults to 'split'; details pane has a mat-table
                data: { skeleton: 'table', listPanel: true }
            },
            {
                path: 'inventory/ingredients',
                loadComponent: () => import('./features/inventory/ingredients/ingredients').then(m => m.IngredientsComponent),
                // ingredients.html: mode="full", mat-table
                data: { skeleton: 'table', listPanel: false }
            },
            {
                path: 'inventory/menu-costs',
                loadComponent: () => import('./features/inventory/menu-costs/menu-costs').then(m => m.MenuCostsComponent),
                // menu-costs.html: mode="full", mat-table
                data: { skeleton: 'table', listPanel: false }
            },
            {
                path: 'inventory/stock-forecast',
                loadComponent: () => import('./features/inventory/stock-forecast/stock-forecast').then(m => m.StockForecastComponent),
                // stock-forecast.html: mode="full", mat-table
                data: { skeleton: 'table', listPanel: false }
            },
            {
                path: 'inventory/product-profitability',
                loadComponent: () => import('./features/inventory/product-profitability/product-profitability').then(m => m.ProductProfitabilityComponent),
                // product-profitability.html: mode="full", mat-table
                data: { skeleton: 'table', listPanel: false }
            },
            {
                path: 'analytics',
                loadComponent: () => import('./features/analytics/analytics.component').then(m => m.AnalyticsComponent),
                // analytics.component.html: mode="full", chart/KPI widget grid
                data: { skeleton: 'dashboard', listPanel: false }
            },
            {
                path: 'activity-log',
                loadComponent: () => import('./features/activity-log/activity-log').then(m => m.ActivityLogComponent),
                // activity-log.html: mode="full", mat-table
                data: { skeleton: 'table', listPanel: false }
            },
            // Support Center
            {
                path: 'support',
                loadComponent: () => import('./features/support/support.component').then(m => m.SupportComponent),
                // support.component.html: <app-master-detail-layout [loading]="loading">
                // with no mode attribute, which defaults to 'split'
                data: { skeleton: 'form', listPanel: true }
            },
            {
                path: 'admin/support',
                loadComponent: () => import('./features/support/support.component').then(m => m.SupportComponent),
                data: { skeleton: 'form', listPanel: true }
            },
        ]
    },
    { path: '**', redirectTo: '' }
];
// Cache bust: 2025-12-17-001
